import { MILLI_IN_UNIT, amountOfLine } from './money'
import { profileBlockReasons, profileReasonText, sameProfile, type SaleProfile } from './profile'
import { vatLabel, type VatCode } from './vat'

/**
 * Корзина продажи: чистые функции без сети и без React. Товар приходит из источника уже с ценой (прайс 1С, вид цены выбрал кассир)
 * и ставкой НДС (с товара). Корзина ничего не угадывает: нет цены или ставки — строка помечается проблемой, и чек не собирается.
 * Откуда товар, корзине не важно: источник даёт SaleProduct с ценой и ставкой, общий код не ветвится по виду источника.
 */

/** Что окно знает о товаре, чтобы положить его в корзину. `null` — источник этого не знает. */
export interface SaleProduct {
  /** Ключ товара в источнике (для 1С — Ref_Key). */
  key: string
  name: string
  sku: string | null
  barcode: string | null
  /** Цена выбранного вида цены в копейках; `null` — в прайсе её нет. */
  priceKopecks: number | null
  /** Ставка НДС товара; `null` — у товара ставка не указана. */
  vat: VatCode | null
  /** Единица измерения для подписи («шт», «кг»); `null` — источник её не знает. */
  unit?: string | null
  /** Весовой товар: количество дробное. Не сказано — штучный. */
  isWeight?: boolean
  /** Фискальный профиль карточки (предмет и способ расчёта, маркировка, единица, источник); `null`/нет — профиля нет, продажа блокируется. */
  profile?: SaleProfile | null
}

export interface CartLine {
  /** Номер строки в корзине: стабилен, пока строка жива (по нему правят и удаляют). */
  id: number
  productKey: string
  name: string
  sku: string | null
  barcode: string | null
  priceKopecks: number | null
  quantityMilli: number
  vat: VatCode | null
  unit: string | null
  isWeight: boolean
  /** Профиль карточки на момент добавления; `null` — профиля нет (строка не уйдёт в чек). */
  profile: SaleProfile | null
}

export interface Cart {
  lines: readonly CartLine[]
  nextId: number
}

export const emptyCart = (): Cart => ({ lines: [], nextId: 1 })

/**
 * Положить товар. Тот же товар по той же цене и ставке складывается в одну строку (количество растёт); иначе — новая строка.
 * Количество — в тысячных долях, положительное.
 */
/** Строка корзины — тот же товар: ключ, цена, ставка и профиль карточки совпали (другой профиль — другая строка). */
export const isSameProduct = (line: CartLine, product: SaleProduct): boolean =>
  line.productKey === product.key &&
  line.priceKopecks === product.priceKopecks &&
  line.vat === product.vat &&
  sameProfile(line.profile, product.profile ?? null)

export function addProduct(cart: Cart, product: SaleProduct, quantityMilli: number = MILLI_IN_UNIT): Cart {
  if (!Number.isSafeInteger(quantityMilli) || quantityMilli <= 0) return cart
  const same = cart.lines.find((line) => isSameProduct(line, product))
  if (same !== undefined) {
    return { ...cart, lines: cart.lines.map((line) => (line === same ? { ...line, quantityMilli: line.quantityMilli + quantityMilli } : line)) }
  }
  const line: CartLine = {
    id: cart.nextId,
    productKey: product.key,
    name: product.name,
    sku: product.sku,
    barcode: product.barcode,
    priceKopecks: product.priceKopecks,
    quantityMilli,
    vat: product.vat,
    unit: product.unit ?? null,
    isWeight: product.isWeight === true,
    profile: product.profile ?? null,
  }
  return { lines: [...cart.lines, line], nextId: cart.nextId + 1 }
}

/** Задать количество строки; ноль и меньше убирают строку. */
export function setQuantity(cart: Cart, lineId: number, quantityMilli: number): Cart {
  if (!Number.isSafeInteger(quantityMilli)) return cart
  if (quantityMilli <= 0) return removeLine(cart, lineId)
  return { ...cart, lines: cart.lines.map((line) => (line.id === lineId ? { ...line, quantityMilli } : line)) }
}

export function removeLine(cart: Cart, lineId: number): Cart {
  return { ...cart, lines: cart.lines.filter((line) => line.id !== lineId) }
}

/** Сумма строки в копейках; без цены — `null`. */
export function lineTotal(line: CartLine): number | null {
  return line.priceKopecks === null ? null : amountOfLine(line.priceKopecks, line.quantityMilli)
}

/** Итог корзины в копейках. Строка без цены в итог не входит, а корзина с такой строкой всё равно не соберётся в чек (`cartProblems`). */
export function cartTotal(cart: Cart): number {
  return cart.lines.reduce((sum, line) => sum + (lineTotal(line) ?? 0), 0)
}

export type CartProblemCode = 'empty' | 'no-price' | 'no-vat' | 'profile-incomplete' | 'marked'

export interface CartProblem {
  code: CartProblemCode
  /** Строка корзины; для пустой корзины `null`. */
  lineId: number | null
  text: string
}

/**
 * Что мешает пробить чек из этой корзины. Тексты — для кассира: куда идти и что делать (ставка НДС исправляется во вкладке
 * «Настройка» номенклатуры, цена — в прайсе 1С).
 */
export function cartProblems(cart: Cart): readonly CartProblem[] {
  if (cart.lines.length === 0) return [{ code: 'empty', lineId: null, text: 'В корзине нет товаров.' }]
  const problems: CartProblem[] = []
  for (const line of cart.lines) {
    if (line.priceKopecks === null) {
      problems.push({ code: 'no-price', lineId: line.id, text: `У товара «${line.name}» нет цены в выбранном виде цены. Выберите другой вид цены или заполните прайс в 1С.` })
    }
    if (line.vat === null) {
      problems.push({
        code: 'no-vat',
        lineId: line.id,
        text: `У товара «${line.name}» не указана ставка НДС. Задайте её во вкладке «Настройка» раздела «Номенклатура».`,
      })
    }
    // Профиль карточки: чего не хватает, названо словами; маркированная позиция — отдельная причина (касса её не принимает).
    const reasons = profileBlockReasons(line.profile)
    if (reasons.includes('marked-not-supported')) {
      problems.push({ code: 'marked', lineId: line.id, text: `Товар «${line.name}» маркированный — касса пока не принимает маркировку. Уберите его из чека.` })
    }
    const missing = reasons.filter((reason) => reason !== 'marked-not-supported')
    if (missing.length > 0) {
      problems.push({
        code: 'profile-incomplete',
        lineId: line.id,
        text: `У товара «${line.name}» ${missing.map(profileReasonText).join(', ')}. Исправьте карточку товара во вкладке «Настройка» раздела «Номенклатура».`,
      })
    }
  }
  return problems
}

/** Ставки НДС, которые встретились в корзине (для подписи «в т. ч. НДС»). */
export function vatCodesIn(cart: Cart): readonly string[] {
  return [...new Set(cart.lines.flatMap((line) => (line.vat === null ? [] : [vatLabel(line.vat)])))]
}
